import React, { useState, useEffect, useCallback } from 'react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import axios from '../../axiosConfig';
import './POSActivityLogs.css';
import {
  Activity,
  User,
  Clock,
  Filter,
  RefreshCw,
  ShoppingCart,
  Package,
  DollarSign,
  Calendar,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Users,
  MapPin,
  X,
  Eye,
  Hash,
  FileText,
  ArrowRight,
  Store
} from 'lucide-react';

// POS-specific activity types
const POS_ACTIVITY_CONFIG = {
  POS_SALE: { icon: ShoppingCart, label: 'POS Sale', color: '#0d7463' },
  POS_REFUND: { icon: DollarSign, label: 'POS Refund', color: '#f59e0b' },
  POS_DISCOUNT_APPLIED: { icon: DollarSign, label: 'Discount Applied', color: '#8b5cf6' },
  POS_VOID: { icon: AlertCircle, label: 'Voided Order', color: '#ef4444' },
  POS_CUSTOM_ITEM: { icon: Package, label: 'Custom Item Added', color: '#8b5cf6' },
  POS_CUSTOM_ITEM_OVERRIDE: { icon: AlertCircle, label: 'Custom Item (Override)', color: '#f59e0b' },
  POS_SHIFT_START: { icon: Clock, label: 'Shift Started', color: '#10b981' },
  POS_SHIFT_END: { icon: Clock, label: 'Shift Ended', color: '#6b7280' },
  POS_CASH_DRAWER_OPEN: { icon: DollarSign, label: 'Cash Drawer Opened', color: '#3b82f6' },
  POS_PAYMENT_RECEIVED: { icon: DollarSign, label: 'Payment Received', color: '#10b981' }
};

// Pre-computed once — keys of the POS-only config map. Was being
// recomputed every render before.
const POS_ACTIVITY_TYPE_KEYS = Object.keys(POS_ACTIVITY_CONFIG);

// ─── Pure formatters ────────────────────────────────────────────
const NGN_FORMATTER = new Intl.NumberFormat('en-NG', {
  style: 'currency',
  currency: 'NGN',
  minimumFractionDigits: 0,
});

function formatCurrency(amount) {
  return NGN_FORMATTER.format(amount || 0);
}

function formatDateTime(dateString) {
  return new Date(dateString).toLocaleString();
}

function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

// ─── Activity-config lookup ─────────────────────────────────────
function getActivityConfig(action) {
  return POS_ACTIVITY_CONFIG[action]
    || { icon: Activity, label: action, color: '#6b7280' };
}

// ─── Detail rows for the modal ──────────────────────────────────
// Takes a `lookupLocationName` so the function stays pure — the
// component passes in a callback that reads from its own `locations`
// state.
function getLogDetails(log, lookupLocationName) {
  const details = [];
  const meta = log.metadata || {};

  switch (log.action) {
    case 'POS_SALE':
      if (meta.orderTotal) details.push({ label: 'Order Total', value: formatCurrency(meta.orderTotal) });
      if (meta.itemCount) details.push({ label: 'Items', value: meta.itemCount });
      if (meta.paymentMethod) details.push({ label: 'Payment Method', value: meta.paymentMethod });
      if (meta.orderNumber) details.push({ label: 'Order Number', value: meta.orderNumber });
      {
        const loc = meta.locationName || lookupLocationName(meta.locationId);
        if (loc) details.push({ label: 'Location', value: loc });
      }
      break;

    case 'POS_REFUND':
      if (meta.refundAmount) details.push({ label: 'Refund Amount', value: formatCurrency(meta.refundAmount) });
      if (meta.reason) details.push({ label: 'Reason', value: meta.reason });
      if (meta.orderNumber) details.push({ label: 'Order Number', value: meta.orderNumber });
      break;

    case 'POS_CUSTOM_ITEM':
    case 'POS_CUSTOM_ITEM_OVERRIDE':
      if (meta.itemName) details.push({ label: 'Item Name', value: meta.itemName });
      if (meta.itemPrice) details.push({ label: 'Price', value: formatCurrency(meta.itemPrice) });
      if (meta.itemQuantity) details.push({ label: 'Quantity', value: meta.itemQuantity });
      if (meta.itemTotal) details.push({ label: 'Total', value: formatCurrency(meta.itemTotal) });
      if (meta.overrideRequired) details.push({ label: 'Override By', value: meta.overrideBy || 'Manager' });
      if (meta.description) details.push({ label: 'Description', value: meta.description });
      break;

    case 'POS_SHIFT_START':
      if (meta.openingCash !== undefined) details.push({ label: 'Opening Cash', value: formatCurrency(meta.openingCash) });
      {
        const loc = meta.locationName || lookupLocationName(meta.locationId);
        if (loc) details.push({ label: 'Location', value: loc });
      }
      break;

    case 'POS_SHIFT_END':
      if (meta.openingCash !== undefined) details.push({ label: 'Opening Cash', value: formatCurrency(meta.openingCash) });
      if (meta.closingCash !== undefined) details.push({ label: 'Closing Cash', value: formatCurrency(meta.closingCash) });
      if (meta.expectedCash !== undefined) details.push({ label: 'Expected Cash', value: formatCurrency(meta.expectedCash) });
      if (meta.cashDifference !== undefined) {
        details.push({
          label: 'Cash Variance',
          value: formatCurrency(meta.cashDifference),
          highlight: meta.cashDifference !== 0
            ? (meta.cashDifference > 0 ? 'over' : 'short')
            : 'balanced',
        });
      }
      if (meta.totalSales !== undefined) details.push({ label: 'Total Sales', value: formatCurrency(meta.totalSales) });
      if (meta.totalOrders !== undefined) details.push({ label: 'Orders', value: meta.totalOrders });
      if (meta.refundCount > 0) details.push({ label: 'Refunds', value: `${meta.refundCount} (${formatCurrency(meta.totalRefunds)})` });
      if (meta.tipTotal > 0) details.push({ label: 'Tips', value: formatCurrency(meta.tipTotal) });
      if (meta.duration !== undefined && meta.duration !== null && !isNaN(meta.duration)) {
        const durationMins = Number(meta.duration);
        const hours = Math.floor(durationMins / 60);
        const mins = Math.round(durationMins % 60);
        details.push({ label: 'Duration', value: `${hours}h ${mins}m` });
      }
      {
        const loc = meta.locationName || lookupLocationName(meta.locationId);
        if (loc) details.push({ label: 'Location', value: loc });
      }
      break;

    case 'POS_DISCOUNT_APPLIED':
      if (meta.discountAmount) details.push({ label: 'Discount Amount', value: formatCurrency(meta.discountAmount) });
      if (meta.discountType) details.push({ label: 'Discount Type', value: meta.discountType });
      if (meta.orderNumber) details.push({ label: 'Order Number', value: meta.orderNumber });
      break;

    case 'POS_VOID':
      if (meta.orderTotal) details.push({ label: 'Voided Amount', value: formatCurrency(meta.orderTotal) });
      if (meta.reason) details.push({ label: 'Reason', value: meta.reason });
      if (meta.orderNumber) details.push({ label: 'Order Number', value: meta.orderNumber });
      break;

    default:
      Object.entries(meta).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
        const isMoney = typeof value === 'number'
          && (key.toLowerCase().includes('amount')
            || key.toLowerCase().includes('total')
            || key.toLowerCase().includes('price')
            || key.toLowerCase().includes('cash'));
        details.push({ label, value: isMoney ? formatCurrency(value) : String(value) });
      });
  }

  return details;
}

// ─── Subcomponents (module-scope per feedback_no_inline_components) ───

function Pagination({ page, pages, onChange }) {
  if (pages <= 1) return null;
  return (
    <div className="pagination">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
      >
        <ChevronLeft size={18} />
      </button>
      <span>Page {page} of {pages}</span>
      <button
        onClick={() => onChange(page + 1)}
        disabled={page >= pages}
        aria-label="Next page"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

// Count by action name from the summary's activityBreakdown array.
function countAction(summary, action) {
  return summary?.activityBreakdown?.find((a) => a.action === action)?.count || 0;
}

function SummaryCards({ summary }) {
  if (!summary) return null;
  const sales = countAction(summary, 'POS_SALE');
  const refunds = countAction(summary, 'POS_REFUND');
  const shiftActivities =
    countAction(summary, 'POS_SHIFT_START') + countAction(summary, 'POS_SHIFT_END');
  const cards = [
    { value: summary.totalActivities || 0, label: 'POS Activities', icon: Activity, color: '#0d7463' },
    { value: sales, label: 'Sales', icon: ShoppingCart, color: '#10b981' },
    { value: refunds, label: 'Refunds', icon: DollarSign, color: '#f59e0b' },
    { value: shiftActivities, label: 'Shift Activities', icon: Clock, color: '#3b82f6' },
  ];
  return (
    <div className="summary-cards">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.label} className="summary-card">
            <div className="card-icon" style={{ background: c.color }}>
              <Icon size={20} />
            </div>
            <div className="card-content">
              <span className="card-value">{c.value}</span>
              <span className="card-label">{c.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const PERIOD_OPTIONS = ['today', 'yesterday', 'week', 'month'];

function PeriodPills({ value, onChange }) {
  return (
    <div className="period-selector" role="tablist" aria-label="Time period">
      {PERIOD_OPTIONS.map((period) => (
        <button
          key={period}
          type="button"
          role="tab"
          aria-selected={value === period}
          className={`period-btn ${value === period ? 'active' : ''}`}
          onClick={() => onChange(period)}
        >
          {period.charAt(0).toUpperCase() + period.slice(1)}
        </button>
      ))}
    </div>
  );
}

function PageHeader({ onToggleFilters, onRefresh, loading }) {
  return (
    <div className="pos-activity-header">
      <div className="header-left">
        <div className="header-icon"><Activity size={24} /></div>
        <div>
          <h1>POS Activity Log</h1>
          <p>Audit trail of every till transaction, shift, and override.</p>
        </div>
      </div>
      <div className="header-actions">
        <button type="button" className="filter-toggle" onClick={onToggleFilters}>
          <Filter size={18} />
          Filters
        </button>
        <button type="button" className="refresh-btn" onClick={onRefresh} disabled={loading} aria-label="Refresh">
          <RefreshCw size={18} className={loading ? 'spin' : ''} />
        </button>
      </div>
    </div>
  );
}

function FiltersPanel({ open, filters, setFilters, teamMembers, onClear }) {
  if (!open) return null;
  const setField = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }));
  return (
    <div className="filters-panel">
      <div className="filter-group">
        <label>Activity Type</label>
        <select value={filters.action} onChange={(e) => setField('action', e.target.value)}>
          <option value="">All POS Activities</option>
          {Object.entries(POS_ACTIVITY_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
      </div>
      <div className="filter-group">
        <label>Team Member</label>
        <select value={filters.teamMemberId} onChange={(e) => setField('teamMemberId', e.target.value)}>
          <option value="">All Members</option>
          {teamMembers.map((member) => (
            <option key={member.id} value={member.id}>
              {member.username || member.email}
            </option>
          ))}
        </select>
      </div>
      <div className="filter-group">
        <label>Date Range</label>
        <div className="date-range">
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value, period: '' }))}
          />
          <span>to</span>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value, period: '' }))}
          />
        </div>
      </div>
      <button type="button" className="clear-filters" onClick={onClear}>Clear Filters</button>
    </div>
  );
}

function LogRow({ log, lookupLocationName, onClick }) {
  const config = getActivityConfig(log.action);
  const Icon = config.icon;
  const performer = log.teamMember?.user?.username
    || log.teamMember?.user?.email
    || log.actorName
    || 'Store Owner';
  const role = log.teamMember?.role?.name || 'Owner';
  const location = log.metadata?.locationName || lookupLocationName(log.metadata?.locationId);
  const orderNumber = log.metadata?.orderNumber;
  const orderTotal = log.metadata?.orderTotal;

  return (
    <div className="log-item clickable" onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}>
      <div className="log-icon" style={{ background: `${config.color}15`, color: config.color }}>
        <Icon size={14} />
      </div>
      <div className="log-content">
        <div className="log-main">
          <span className="log-action" style={{ color: config.color }}>{config.label}</span>
          {orderNumber && <span className="log-target">#{orderNumber}</span>}
          {orderTotal != null && (
            <span className="log-amount">{formatCurrency(orderTotal)}</span>
          )}
        </div>
        <div className="log-meta">
          <span className="log-performer">{performer}</span>
          <span className="log-dot" aria-hidden>·</span>
          <span className="log-role">{role}</span>
          {location && (
            <>
              <span className="log-dot" aria-hidden>·</span>
              <span className="log-location"><MapPin size={11} />{location}</span>
            </>
          )}
          <span className="log-dot" aria-hidden>·</span>
          <span className="log-time"><Clock size={11} />{formatRelativeTime(log.createdAt)}</span>
        </div>
      </div>
      <div className="log-view-btn" aria-label="View details">
        <Eye size={14} />
      </div>
    </div>
  );
}

function LogList({ loading, logs, lookupLocationName, onSelect, pagination, onPageChange }) {
  if (loading) {
    return (
      <div className="activity-logs">
        <div className="loading-state">
          <RefreshCw className="spin" size={24} />
          <p>Loading activities...</p>
        </div>
      </div>
    );
  }
  if (logs.length === 0) {
    return (
      <div className="activity-logs">
        <div className="empty-state">
          <Store size={48} />
          <h3>No POS activities found</h3>
          <p>POS transactions and shift activities will appear here.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="activity-logs">
      <div className="logs-list">
        {logs.map((log) => (
          <LogRow
            key={log.id}
            log={log}
            lookupLocationName={lookupLocationName}
            onClick={() => onSelect(log)}
          />
        ))}
      </div>
      <Pagination page={pagination.page} pages={pagination.pages} onChange={onPageChange} />
    </div>
  );
}

function LogDetailsModal({ log, lookupLocationName, onClose }) {
  if (!log) return null;
  const config = getActivityConfig(log.action);
  const Icon = config.icon;
  const performer = log.teamMember?.user?.username
    || log.teamMember?.user?.email
    || log.actorName
    || 'Store Owner';
  const role = log.teamMember?.role?.name || 'Owner';
  const details = getLogDetails(log, lookupLocationName);

  return (
    <div className="log-modal-overlay" onClick={onClose}>
      <div className="log-modal" onClick={(e) => e.stopPropagation()}>
        <div className="log-modal-header">
          <div className="modal-header-left">
            <div className="modal-icon" style={{ background: `${config.color}15`, color: config.color }}>
              <Icon size={24} />
            </div>
            <div>
              <h2>{config.label}</h2>
              <p className="modal-subtitle">By {performer}</p>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="log-modal-body">
          <div className="modal-section">
            <h3>Activity Information</h3>
            <div className="modal-info-grid">
              <div className="info-item">
                <span className="info-label"><Clock size={14} /> Time</span>
                <span className="info-value">{formatDateTime(log.createdAt)}</span>
              </div>
              <div className="info-item">
                <span className="info-label"><User size={14} /> Performer</span>
                <span className="info-value">{performer}</span>
              </div>
              <div className="info-item">
                <span className="info-label"><Users size={14} /> Role</span>
                <span className="info-value">{role}</span>
              </div>
              {log.metadata?.orderNumber && (
                <div className="info-item">
                  <span className="info-label"><FileText size={14} /> Order</span>
                  <span className="info-value">#{log.metadata.orderNumber}</span>
                </div>
              )}
              {log.targetType && (
                <div className="info-item">
                  <span className="info-label"><FileText size={14} /> Target</span>
                  <span className="info-value">{log.targetType}: {log.targetId}</span>
                </div>
              )}
            </div>
          </div>

          {details.length > 0 && (
            <div className="modal-section">
              <h3>Details</h3>
              <div className="modal-details-list">
                {details.map((detail, idx) => (
                  <div
                    key={idx}
                    className={`detail-row ${detail.highlight ? `highlight-${detail.highlight}` : ''}`}
                  >
                    <span className="detail-label">{detail.label}</span>
                    <span className="detail-value">{detail.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(log.previousValue || log.newValue) && (
            <div className="modal-section">
              <h3>Value Changes</h3>
              <div className="value-changes">
                {log.previousValue && (
                  <div className="value-box previous">
                    <span className="value-label">Previous</span>
                    <pre>{JSON.stringify(log.previousValue, null, 2)}</pre>
                  </div>
                )}
                {log.previousValue && log.newValue && (
                  <ArrowRight size={20} className="change-arrow" />
                )}
                {log.newValue && (
                  <div className="value-box new">
                    <span className="value-label">New</span>
                    <pre>{JSON.stringify(log.newValue, null, 2)}</pre>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="modal-section technical">
            <h3>Technical Info</h3>
            <div className="technical-info">
              <div className="tech-row">
                <span className="tech-label"><Hash size={12} /> Log ID</span>
                <span className="tech-value">{log.id}</span>
              </div>
              {log.ipAddress && (
                <div className="tech-row">
                  <span className="tech-label"><MapPin size={12} /> IP Address</span>
                  <span className="tech-value">{log.ipAddress}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const POSActivityLogs = () => {
  const { isAuthenticated } = useSecureAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    action: '',
    teamMemberId: '',
    period: 'today',
    startDate: '',
    endDate: ''
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // Helper to get location name from ID — memoized so passing it
  // as a prop to child components doesn't cause spurious re-renders.
  const getLocationName = useCallback((locationId) => {
    if (!locationId) return null;
    const location = locations.find((l) => l.id === locationId);
    return location?.name || null;
  }, [locations]);

  // Fetch activity summary
  const fetchSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('period', filters.period);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await axios.get(`/team-activity/summary?${params}`);
      if (response.data.success) {
        // Filter summary for POS activities only
        const fullSummary = response.data.summary;
        const posBreakdown = fullSummary.activityBreakdown?.filter(
          a => POS_ACTIVITY_TYPE_KEYS.includes(a.action)
        ) || [];
        const totalPosActivities = posBreakdown.reduce((sum, a) => sum + a.count, 0);

        setSummary({
          ...fullSummary,
          totalActivities: totalPosActivities,
          activityBreakdown: posBreakdown
        });
        setTeamMembers(fullSummary.teamMembers || []);
      }
    } catch (error) {
      console.error('Failed to fetch activity summary:', error);
    }
  }, [filters.period, filters.startDate, filters.endDate]);

  // Fetch activity logs
  const fetchLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', pagination.limit);
      params.append('actionPrefix', 'POS_'); // Filter for POS activities

      if (filters.action) params.append('action', filters.action);
      if (filters.teamMemberId) params.append('teamMemberId', filters.teamMemberId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.period && !filters.startDate && !filters.endDate) {
        params.append('period', filters.period);
      }

      const response = await axios.get(`/team-activity/logs?${params}`);
      if (response.data.success) {
        // Filter to only POS activities
        const posLogs = (response.data.logs || []).filter(
          log => POS_ACTIVITY_TYPE_KEYS.includes(log.action)
        );
        setLogs(posLogs);
        setPagination(response.data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Fetch locations for name lookup
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get('/locations');
        if (response.data?.success) {
          setLocations(response.data.locations || []);
        }
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    };
    if (isAuthenticated) {
      fetchLocations();
    }
  }, [isAuthenticated]);

  // Initial load + refetch when filters change. `filters` covers
  // both the initial mount and subsequent filter changes; `fetchLogs`
  // and `fetchSummary` are both `useCallback` so they're stable as
  // long as their own deps don't move.
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchLogs(1);
    fetchSummary();
  }, [isAuthenticated, filters, fetchLogs, fetchSummary]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      fetchLogs(newPage);
    }
  };

  // Handle period filter change
  const handlePeriodChange = (period) => {
    setFilters(prev => ({ ...prev, period, startDate: '', endDate: '' }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      action: '',
      teamMemberId: '',
      period: 'today',
      startDate: '',
      endDate: ''
    });
  };

  return (
    <Shell>
      <FeatureGate featureId="sales.pos" fallback={
        <div className="feature-locked">
          <Store size={48} />
          <h2>POS Activity Logs</h2>
          <p>Track all point of sale transactions and shift activities.</p>
          <p className="upgrade-notice">This feature is available on PRO and PREMIUM plans.</p>
        </div>
      }>
        <div className="pos-activity-container">
          <PageHeader
            onToggleFilters={() => setShowFilters((s) => !s)}
            onRefresh={() => { fetchLogs(pagination.page); fetchSummary(); }}
            loading={loading}
          />
          <PeriodPills value={filters.period} onChange={handlePeriodChange} />
          <FiltersPanel
            open={showFilters}
            filters={filters}
            setFilters={setFilters}
            teamMembers={teamMembers}
            onClear={clearFilters}
          />
          <SummaryCards summary={summary} />
          <LogList
            loading={loading}
            logs={logs}
            lookupLocationName={getLocationName}
            onSelect={setSelectedLog}
            pagination={pagination}
            onPageChange={handlePageChange}
          />
          <LogDetailsModal
            log={selectedLog}
            lookupLocationName={getLocationName}
            onClose={() => setSelectedLog(null)}
          />
        </div>
      </FeatureGate>
    </Shell>
  );
};

export default POSActivityLogs;
